import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useTranslation } from 'react-i18next';

/**
 * Screenshot slot. Renders nothing if there is no image or the image fails to load,
 * so a missing asset degrades to a text-only guide instead of a broken box.
 *
 * The thumbnail is cropped to 16:9 and too small to read, so clicking it opens the
 * full screenshot in a lightbox.
 */
export function GuideShot({ src, alt }) {
  const { t } = useTranslation();
  const theme = useTheme();
  // A landscape screenshot inside a portrait dialog is barely larger than the thumbnail,
  // so on phones we go full screen and scale to the height, letting the user pan sideways.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  // Scaled to the height, the image opens on the app's nav sidebar, which is the least
  // useful part of every one of these screenshots. Start in the middle instead.
  const centerScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollLeft = Math.max(0, (node.scrollWidth - node.clientWidth) / 2);
  }, []);

  if (!src || failed) return null;

  const label = alt || t('onboardingChecklist.enlargeImage');

  return (
    <>
      <ButtonBase
        focusRipple
        onClick={() => setOpen(true)}
        aria-label={t('onboardingChecklist.enlargeImage')}
        data-testid="guide-shot-open"
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          textAlign: 'left',
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: 'zoom-in',
          transition: 'border-color 180ms',
          '&:hover': { borderColor: 'secondary.main' },
          '&:hover .guide-shot-zoom, &.Mui-focusVisible .guide-shot-zoom': { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt || ''}
          onError={() => setFailed(true)}
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            objectPosition: 'top center',
            display: 'block',
          }}
        />
        <Box
          className="guide-shot-zoom"
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(15, 35, 64, 0.72)',
            color: '#fff',
            opacity: 0,
            transition: 'opacity 180ms',
            pointerEvents: 'none',
          }}
        >
          <ZoomInIcon sx={{ fontSize: 18 }} />
        </Box>
      </ButtonBase>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        data-testid="guide-shot-dialog"
        aria-label={label}
        TransitionProps={{ onEntered: centerScroll }}
      >
        <DialogContent
          ref={scrollRef}
          sx={{
            p: 0,
            position: 'relative',
            lineHeight: 0,
            bgcolor: 'background.default',
            overflow: fullScreen ? 'auto' : 'hidden',
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={t('onboardingChecklist.close')}
            data-testid="guide-shot-close"
            sx={{
              // Fixed on phones so it stays reachable while panning across the image.
              position: fullScreen ? 'fixed' : 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'rgba(15, 35, 64, 0.72)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(15, 35, 64, 0.88)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={src}
            alt={alt || ''}
            onLoad={centerScroll}
            sx={
              fullScreen
                ? { height: '100%', width: 'auto', maxWidth: 'none', display: 'block' }
                : { width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain', display: 'block' }
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

/** One "how it is done" row. */
export function GuideWay({ icon: Icon, title, description }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1,
          flexShrink: 0,
          mt: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(49, 130, 206, 0.1)',
          color: 'secondary.main',
        }}
      >
        {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
        <Typography sx={{ fontSize: '12.5px', lineHeight: 1.5, color: 'text.secondary', mt: '2px' }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

/** Two-column guide layout that stacks on mobile. */
export function GuideLayout({ children, shot, shotAlt }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>{children}</Box>
      <GuideShot src={shot} alt={shotAlt} />
    </Box>
  );
}

/**
 * Generic guide panel: intro + three "ways" + a CTA that deep-links into the app.
 */
function GuideStepPanel({ intro, ways, ctaLabel, ctaWhere, ctaIcon: CtaIcon, onCta, ctaDisabled, ctaHint, shot, shotAlt, footer }) {
  return (
    <GuideLayout shot={shot} shotAlt={shotAlt}>
      <Typography sx={{ fontSize: '13.5px', lineHeight: 1.6 }}>{intro}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {(ways || []).map((way, index) => (
          <GuideWay key={index} icon={way.icon} title={way.title} description={way.description} />
        ))}
      </Box>
      {footer}
      {ctaLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 0.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={CtaIcon ? <CtaIcon /> : null}
            endIcon={<ArrowForwardIcon />}
            onClick={onCta}
            disabled={ctaDisabled}
            sx={{ textTransform: 'none' }}
          >
            {ctaLabel}
          </Button>
          {ctaWhere && (
            <Typography sx={{ fontSize: '12px', color: 'text.disabled', fontWeight: 600 }}>{ctaWhere}</Typography>
          )}
        </Box>
      )}
      {ctaHint && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{ctaHint}</Typography>
      )}
    </GuideLayout>
  );
}

export default GuideStepPanel;
